"use client";

import * as React from "react";
import { cn } from "../../utils";
import { RichChipEditor } from "../rich-chip-editor";
import {
  MENTION_CHIP_NODE,
  MENTION_CHIP_STYLES,
  createMentionChipExtension,
  type Mentionable,
} from "../mention-chip-editor";

export interface MarkdownEditorProps {
  /** Current value as a markdown string. Linear's issue `description` field. */
  value?: string | null;
  /** Called with the serialized markdown string on every edit. */
  onChange?: (value: string) => void;
  /** Called with the current markdown when the editor loses focus. */
  onBlur?: (value: string) => void;
  onFocus?: () => void;
  /** Placeholder text when the editor is empty. */
  placeholder?: string;
  /** Disable editing. */
  disabled?: boolean;
  /** When false, the document still renders but is not editable. */
  editable?: boolean;
  /** Editor min-height in rows. Used by the boxed form variant only. */
  rows?: number;
  /**
   * `boxed` is a form control (border, min-height).
   * `seamless` is the Linear-style surface: no chrome, so focusing it only
   * adds a caret. Layout must match compact MarkdownRenderer.
   */
  variant?: "boxed" | "seamless";
  /** Accessible name. Seamless surfaces have no visible label. */
  "aria-label"?: string;
  /** Associates a visible `<Label htmlFor>` with the editor surface. */
  id?: string;
  /** Marks the editor surface invalid, e.g. a value past a server-side cap. */
  "aria-invalid"?: boolean;
  /** Points the editor surface at the message describing why it is invalid. */
  "aria-describedby"?: string;
  /** When set, `@` inserts a mention chip. Insert-only — no notify/run. */
  mentionOptions?: Mentionable[];
  className?: string;
}

const LINEAR_LINK_ATTRS = { rel: "noopener noreferrer nofollow" };

/** Markdown-string preset of {@link RichChipEditor}. Seamless = caret only. */
export function MarkdownEditor({
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  disabled,
  editable,
  rows = 8,
  variant = "boxed",
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  id,
  mentionOptions,
  className,
}: MarkdownEditorProps) {
  const seamless = variant === "seamless";
  const mentionOptionsRef = React.useRef(mentionOptions);
  mentionOptionsRef.current = mentionOptions;
  const hasMentions = mentionOptions !== undefined;
  const mentionExtension = React.useMemo(
    () =>
      hasMentions ? createMentionChipExtension(() => mentionOptionsRef.current ?? []) : undefined,
    [hasMentions],
  );

  return (
    <RichChipEditor
      value={value}
      valueFormat="markdown"
      variant={variant}
      {...(onChange ? { onChange } : {})}
      {...(onBlur ? { onBlur } : {})}
      {...(onFocus ? { onFocus } : {})}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(disabled !== undefined ? { disabled } : {})}
      {...(editable !== undefined ? { editable } : {})}
      rows={rows}
      {...(ariaLabel !== undefined ? { ariaLabel } : {})}
      {...(ariaInvalid !== undefined ? { ariaInvalid } : {})}
      {...(ariaDescribedBy !== undefined ? { ariaDescribedBy } : {})}
      {...(id !== undefined ? { id } : {})}
      linearMarkdown
      publicIdLinks
      linkHTMLAttributes={LINEAR_LINK_ATTRS}
      {...(mentionExtension
        ? {
            chipExtension: mentionExtension,
            chipNode: MENTION_CHIP_NODE,
            chipStyles: MENTION_CHIP_STYLES,
          }
        : {})}
      className={cn(
        "markdown-editor",
        seamless ? "markdown-editor--seamless" : "markdown-editor--boxed",
        className,
      )}
    />
  );
}
